import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { setupAuthInterceptors } from '../services/authInterceptors';

const AuthContext = createContext();

const TOKEN_KEY = 'token';
const REFRESH_KEY = 'refresh_token';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const sessionExpiredRef = useRef(false);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common.Authorization;
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    clearSession();
    // Вернём флаг после редиректа на логин (новый логин)
    window.setTimeout(() => {
      sessionExpiredRef.current = false;
    }, 500);
  }, [clearSession]);

  useEffect(() => {
    setupAuthInterceptors({ onSessionExpired: handleSessionExpired });
  }, [handleSessionExpired]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }

      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      try {
        const response = await axios.get('/api/auth/me');
        if (!cancelled && response.data?.id) {
          setUser(response.data);
        }
      } catch (error) {
        // Интерцептор мог уже обновить токен и повторить запрос;
        // если всё равно ошибка — сессия сброшена в onSessionExpired.
        if (!cancelled && error.response?.status === 401) {
          clearSession();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [token, clearSession]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email: (email || '').trim(),
        password,
      });
      const { access_token, refresh_token, user: userData } = response.data;
      localStorage.setItem(TOKEN_KEY, access_token);
      if (refresh_token) {
        localStorage.setItem(REFRESH_KEY, refresh_token);
      }
      sessionExpiredRef.current = false;
      setToken(access_token);
      axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ошибка входа',
      };
    }
  };

  const logout = () => {
    clearSession();
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
